import React, { useState, useEffect } from 'react';
import { Calendar, Clock, ShoppingBasket, MapPin, Car, Phone, Mail, ChevronLeft, ChevronRight, X, Sun, Moon, Users, Star, Heart } from 'lucide-react';

interface Feature {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
}

interface PricingPlan {
  id: string;
  label: string;
  price: string;
}

interface MousePosition {
  x: number;
  y: number;
}

const StrawberryPickingSite: React.FC = () => {
  const [darkMode, setDarkMode] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<number | null>(null);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [galleryOpen, setGalleryOpen] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<number>(0);
  const [scrollY, setScrollY] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [bookingComplete, setBookingComplete] = useState<boolean>(false);
  const [mousePosition, setMousePosition] = useState<MousePosition>({ x: 0, y: 0 });

  useEffect(() => {
    const handleScroll = (): void => setScrollY(window.scrollY);
    const handleMouseMove = (e: MouseEvent): void => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
  }, [darkMode]);

  const features: Feature[] = [
    { icon: Clock, title: '30分食べ放題', description: '時間を気にせずゆっくりお楽しみいただけます' },
    { icon: Calendar, title: '完全予約制', description: '混雑を避けて快適な体験を' },
    { icon: ShoppingBasket, title: '手ぶらでOK', description: '必要なものは全て用意しています' }
  ];

  const pricing: PricingPlan[] = [
    { id: 'adult', label: '大人（中学生以上）', price: '¥3,000' },
    { id: 'elementary', label: '小学生', price: '¥2,500' },
    { id: 'child', label: '幼児（2歳〜未就学児）', price: '¥1,500' },
    { id: 'baby', label: '0〜1歳', price: '無料' }
  ];

  const galleryImages: string[] = [
    '🍓 完熟いちご',
    '🌱 いちご畑',
    '👨‍👩‍👧‍👦 家族での体験',
    '🧺 収穫バスケット',
    '🏞️ 農園風景',
    '🍰 いちごスイーツ'
  ];

  const getDaysInMonth = (date: Date): (number | null)[] => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days: (number | null)[] = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    
    return days;
  };

  const handleBooking = (): void => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setBookingComplete(true);
      // Confetti effect
      setTimeout(() => setBookingComplete(false), 3000);
    }, 2000);
  };

  const scrollToBooking = (): void => {
    const element = document.getElementById('booking');
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className={`min-h-screen transition-colors duration-500 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-pink-500/30 rounded-full animate-spin" />
            <div className="absolute inset-0 w-20 h-20 border-4 border-pink-500 rounded-full animate-spin border-t-transparent" />
          </div>
        </div>
      )}

      {/* Confetti Effect */}
      {bookingComplete && (
        <div className="fixed inset-0 z-50 pointer-events-none">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-bounce"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${2 + Math.random() * 2}s`
              }}
            >
              <div className={`text-2xl ${Math.random() > 0.5 ? 'animate-spin' : ''}`}>
                {Math.random() > 0.5 ? '🍓' : '🎉'}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dark Mode Toggle */}
      <button
        onClick={() => setDarkMode(!darkMode)}
        className="fixed top-4 right-4 z-40 p-3 bg-white/10 backdrop-blur-lg rounded-full shadow-lg hover:scale-110 transition-transform duration-300"
        aria-label="Toggle dark mode"
      >
        {darkMode ? <Sun className="w-6 h-6 text-yellow-400" /> : <Moon className="w-6 h-6 text-gray-800" />}
      </button>

      {/* Hero Section with Parallax */}
      <section className="relative h-screen overflow-hidden">
        {/* Animated Background */}
        <div 
          className="absolute inset-0 bg-gradient-to-br from-pink-400 via-red-400 to-pink-500"
          style={{ transform: `translateY(${scrollY * 0.5}px)` }}
        >
          {/* Floating Strawberries */}
          {[...Array(10)].map((_, i) => (
            <div
              key={i}
              className="absolute text-6xl animate-bounce"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${3 + Math.random() * 3}s`
              }}
            >
              🍓
            </div>
          ))}
        </div>

        {/* Mouse Follower Particles */}
        <div 
          className="absolute w-20 h-20 bg-white/20 rounded-full blur-xl pointer-events-none transition-all duration-300 ease-out"
          style={{
            left: mousePosition.x - 40,
            top: mousePosition.y - 40,
            transform: `translate(${scrollY * 0.1}px, ${scrollY * 0.1}px)`
          }}
        />

        {/* Hero Content */}
        <div className="relative z-10 h-full flex items-center justify-center px-4">
          <div className="text-center space-y-8 animate-fade-in">
            <h1 className="text-5xl md:text-7xl font-bold text-white drop-shadow-lg">
              <span className="inline-block hover:rotate-12 transition-transform duration-300">摘みたて</span>
              <span className="inline-block hover:-rotate-12 transition-transform duration-300">完熟</span>
              <br />
              <span className="bg-gradient-to-r from-yellow-300 to-pink-300 bg-clip-text text-transparent">
                いちごの贅沢な時間
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-2xl mx-auto">
              甘くてジューシーな完熟いちごを心ゆくまでお楽しみください
            </p>
            <button
              onClick={scrollToBooking}
              className="group relative px-8 py-4 bg-white/20 backdrop-blur-lg rounded-full text-white font-bold text-lg overflow-hidden hover:scale-105 transition-all duration-300"
            >
              <span className="relative z-10">今すぐ予約する</span>
              <div className="absolute inset-0 bg-gradient-to-r from-pink-500 to-red-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </button>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronLeft className="w-8 h-8 text-white rotate-90" />
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16 bg-gradient-to-r from-pink-500 to-red-500 bg-clip-text text-transparent">
            特別な体験をお約束
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group relative p-8 bg-white/10 dark:bg-gray-800/30 backdrop-blur-lg rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-pink-500/20 to-red-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative z-10">
                  <div className="mb-6 inline-flex p-4 bg-gradient-to-br from-pink-500 to-red-500 rounded-2xl shadow-lg group-hover:scale-110 group-hover:rotate-12 transition-all duration-500">
                    <feature.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3 text-gray-800 dark:text-white">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-transparent via-pink-50/50 dark:via-pink-900/20 to-transparent">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16 bg-gradient-to-r from-pink-500 to-red-500 bg-clip-text text-transparent">
            料金プラン
          </h2>
          <div className="grid md:grid-cols-4 gap-6">
            {pricing.map((plan, index) => (
              <div
                key={plan.id}
                className="relative p-6 rounded-3xl transition-all duration-500 hover:scale-105 bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <h3 className="text-lg font-medium mb-2 text-gray-700 dark:text-gray-300">
                  {plan.label}
                </h3>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">
                  {plan.price}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Calendar */}
      <section id="booking" className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16 bg-gradient-to-r from-pink-500 to-red-500 bg-clip-text text-transparent">
            ご予約カレンダー
          </h2>
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-3xl shadow-2xl p-8">
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-8">
              <button
                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors duration-300"
                aria-label="Previous month"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <h3 className="text-2xl font-bold text-gray-800 dark:text-white">
                {currentMonth.toLocaleDateString('ja-JP', { year: 'numeric', month: 'long' })}
              </h3>
              <button
                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors duration-300"
                aria-label="Next month"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-2 mb-8">
              {['日', '月', '火', '水', '木', '金', '土'].map(day => (
                <div key={day} className="text-center text-sm font-medium text-gray-500 dark:text-gray-400 py-2">
                  {day}
                </div>
              ))}
              {getDaysInMonth(currentMonth).map((day, index) => (
                <div key={index} className="aspect-square">
                  {day && (
                    <button
                      onClick={() => setSelectedDate(day)}
                      disabled={day < 15}
                      className={`w-full h-full rounded-xl transition-all duration-300 ${
                        selectedDate === day
                          ? 'bg-gradient-to-br from-pink-500 to-red-500 text-white scale-110 shadow-lg'
                          : day < 15
                          ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                          : 'bg-white dark:bg-gray-600 hover:bg-pink-100 dark:hover:bg-pink-900 hover:scale-105'
                      }`}
                    >
                      {day}
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Booking Button */}
            {selectedDate && (
              <button
                onClick={handleBooking}
                className="w-full py-4 bg-gradient-to-r from-pink-500 to-red-500 text-white font-bold text-lg rounded-2xl hover:shadow-xl hover:scale-105 transition-all duration-300"
              >
                予約を確定する
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Access Information */}
      <section className="py-20 px-4 bg-gradient-to-b from-transparent to-pink-50/50 dark:to-pink-900/20">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16 bg-gradient-to-r from-pink-500 to-red-500 bg-clip-text text-transparent">
            アクセス情報
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-3xl p-8 shadow-xl">
              <h3 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white flex items-center">
                <MapPin className="w-6 h-6 mr-2 text-pink-500" />
                農園情報
              </h3>
              <div className="space-y-4">
                <p className="text-gray-600 dark:text-gray-300">
                  <span className="font-medium">住所：</span>八王子市七国1-14-2
                </p>
                <p className="text-gray-600 dark:text-gray-300">
                  <span className="font-medium">最寄駅：</span>JR横浜線みなみ野駅より徒歩10分
                </p>
                <p className="text-gray-600 dark:text-gray-300 flex items-center">
                  <Car className="w-5 h-5 mr-2 text-pink-500" />
                  <span className="font-medium">駐車場：</span>4台（1グループ1台）
                </p>
                <div className="pt-4 space-y-2">
                  <p className="text-gray-600 dark:text-gray-300 flex items-center">
                    <Phone className="w-5 h-5 mr-2 text-pink-500" />
                    090-1234-5678
                  </p>
                  <p className="text-gray-600 dark:text-gray-300 flex items-center">
                    <Mail className="w-5 h-5 mr-2 text-pink-500" />
                    info@strawberry-farm.jp
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-3xl p-8 shadow-xl">
              <div className="w-full h-64 bg-gray-200 dark:bg-gray-700 rounded-xl flex items-center justify-center">
                <MapPin className="w-16 h-16 text-gray-400" />
                <p className="ml-4 text-gray-500">インタラクティブマップ</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16 bg-gradient-to-r from-pink-500 to-red-500 bg-clip-text text-transparent">
            フォトギャラリー
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {galleryImages.map((image, index) => (
              <div
                key={index}
                onClick={() => {
                  setSelectedImage(index);
                  setGalleryOpen(true);
                }}
                className="relative aspect-square bg-gradient-to-br from-pink-100 to-red-100 dark:from-pink-900 dark:to-red-900 rounded-2xl overflow-hidden cursor-pointer hover:scale-105 transition-transform duration-300 group"
              >
                <div className="absolute inset-0 flex items-center justify-center text-6xl group-hover:scale-110 transition-transform duration-300">
                  {image.split(' ')[0]}
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/50 to-transparent">
                  <p className="text-white font-medium">{image.split(' ')[1]}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Lightbox */}
      {galleryOpen && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4">
          <button
            onClick={() => setGalleryOpen(false)}
            className="absolute top-4 right-4 p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors duration-300"
            aria-label="Close gallery"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <div className="relative max-w-4xl w-full">
            <div className="aspect-video bg-gradient-to-br from-pink-200 to-red-200 dark:from-pink-800 dark:to-red-800 rounded-2xl flex items-center justify-center text-9xl">
              {galleryImages[selectedImage].split(' ')[0]}
            </div>
            <p className="text-center text-white text-2xl mt-4">
              {galleryImages[selectedImage]}
            </p>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="py-8 px-4 bg-gray-100 dark:bg-gray-900">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-gray-600 dark:text-gray-400">
            © 2024 いちご農園 - 摘みたて完熟いちごの贅沢な時間
          </p>
          <div className="flex justify-center space-x-4 mt-4">
            <Heart className="w-6 h-6 text-pink-500 animate-pulse" />
            <Star className="w-6 h-6 text-yellow-500 animate-spin-slow" />
            <Users className="w-6 h-6 text-blue-500 animate-bounce" />
          </div>
        </div>
      </footer>
    </div>
  );
};

export default StrawberryPickingSite;